import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { rememberMemberEmail } from "@/lib/memberEmail";
import { MembershipPause } from "@/components/membership/MembershipPause";
import { ReferralCard } from "@/components/membership/ReferralCard";
import { BirthdayCard } from "@/components/membership/BirthdayCard";
import { MembershipStats } from "@/components/membership/MembershipStats";
import { MembershipCancelBlock } from "@/components/membership/MembershipCancelBlock";
import { statusPresentation } from "@/components/membership/statusPresentation";

function IncludedSession({ m }) {
  if (!m.included_service) return null;
  return (
    <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/50 p-4 text-sm" data-testid="membership-included">
      {m.included_available
        ? <>Your included 30-minute session for this month is <strong className="text-[#FFD600]">ready</strong> — <Link to="/book?service=30-min" className="dg-link">book it</Link> with your member email and it'll show as $0.</>
        : <>You've used this month's included session. It resets on the 1st.</>}
    </div>
  );
}

export default function MembershipStatus() {
  const { token } = useParams();
  const [m, setM] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let tries = 0;
    const load = () => api.get(`/memberships/public/${token}`).then((r) => {
      setM(r.data);
      if (r.data.status === "active" || r.data.status === "cancelling") rememberMemberEmail(r.data.email);
      if (r.data.status === "pending" && tries++ < 20) setTimeout(load, 5000);
    }).catch(() => setError("We couldn't find that membership."));
    load();
  }, [token]);

  if (error) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-300" data-testid="membership-notfound">{error} <Link to="/membership" className="dg-link">See membership plans</Link></div>;
  if (!m) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-400">Loading…</div>;
  const { live, Icon, tone, label, title } = statusPresentation(m);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="membership-status">
      <div className="dg-card rounded-2xl p-8">
        <div className="flex items-start gap-4">
          <Icon className={`w-10 h-10 shrink-0 ${tone}`} />
          <div className="flex-1">
            <div className="dg-eyebrow" data-testid="membership-status-label">{label}</div>
            <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold font-display" data-testid="membership-title">{title}</h1>
            <p className="text-slate-300 mt-3">{m.tier_name} · ${Number(m.price_usd).toFixed(2)}/month · <span className="text-slate-400">{m.email}</span></p>
            {m.charged_through_date && live && <p className="text-sm text-slate-400 mt-1" data-testid="membership-paid-through">Paid through {m.charged_through_date}{m.status === "cancelling" ? " — perks end then, no further charges" : " — renews automatically"}</p>}
          </div>
        </div>

        {m.status === "pending" && (
          <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100" data-testid="membership-pending">
            <p>We're waiting for Stripe to confirm your first payment. This page updates by itself — if you closed the checkout, <a href={m.checkout_url} className="dg-link">finish paying here</a>.</p>
          </div>
        )}

        <MembershipPause m={m} token={token} onChange={setM} />

        {live && (
          <>
            <ul className="mt-6 grid gap-2 sm:grid-cols-2 text-sm text-slate-200" data-testid="membership-perks">
              {m.perks.map((p) => <li key={p} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#FFD600] mt-0.5 shrink-0" /> {p}</li>)}
            </ul>
            <IncludedSession m={m} />
            <MembershipStats stats={m.stats} />
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/ask" className="dg-btn-primary rounded-full px-5 py-2.5 text-sm" data-testid="membership-ask-btn">Ask Dr.Gadget (priority)</Link>
              <Link to="/book" className="dg-btn-ghost rounded-full px-5 py-2.5 text-sm" data-testid="membership-book-btn">Book a session — 10% off</Link>
            </div>
            <ReferralCard m={m} />
            <BirthdayCard key={m.birthday_md || "none"} m={m} token={token} onChange={setM} />
          </>
        )}

        <MembershipCancelBlock m={m} token={token} onChange={setM} />
        {(m.status === "cancelled" || m.status === "deactivated") && (
          <div className="mt-6"><Link to="/membership" className="dg-btn-primary rounded-full px-6 py-3 text-sm inline-flex" data-testid="membership-rejoin-btn">Rejoin</Link></div>
        )}
        <p className="mt-6 text-xs text-slate-500">Billing is handled by Stripe — receipts come from Stripe. Questions? Call <a href="tel:+13104220669" className="dg-link">310-422-0669</a>.</p>
      </div>
    </div>
  );
}
